import React, { useState, useEffect } from 'react';
import { leadApi } from '../../services/apis/configuredApis';
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from '../../services/linkedin';
import { trackGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from '../../services/googleAds';
import { isBlockedEmailDomain } from '../../utils/blockedEmailDomains';

// Country/Region options: USA first, then continents
const COUNTRY_REGION_OPTIONS = [
  'United States of America',
  'North America',
  'South America',
  'Europe',
  'Asia',
  'Africa',
  'Oceania',
  'Middle East',
];

/**
 * Standard lead magnet landing template — original form-driven layout that
 * captures email/contact info before unlocking a downloadable asset.
 */
function StandardLandingTemplate({ leadMagnet }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    jobTitle: '',
    phoneNumber: '',
    email: '',
    countryRegion: '',
    consentGiven: false
  });

  useEffect(() => {
    setSubmitted(false);
    setErrors({});
    setTouched({});
    setDownloadTriggered(false);
    setDownloadComplete(false);
  }, [leadMagnet?.leadMagnetId]);

  const validateEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    if (isBlockedEmailDomain(email)) {
      return { isValid: false, message: 'Please use your work email' };
    }
    return { isValid: true };
  };

  const validatePhone = (phone) => {
    if (!phone) return { isValid: false, message: 'Phone number is required' };
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 10) return { isValid: true };
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) return { isValid: true };
    return { isValid: false, message: 'Please enter a valid US phone number (e.g. 555-123-4567)' };
  };

  const validateField = (name, value) => {
    const formConfig = leadMagnet?.formConfig || {};
    const isRequired = formConfig[`${name}Required`] !== false;

    if (name === 'jobTitle') {
      const jobTitleRequired = formConfig.jobTitleRequired === true;
      if (jobTitleRequired && !value?.trim()) {
        return { isValid: false, message: 'Job title is required' };
      }
      return { isValid: true };
    }

    if (name === 'consentGiven') {
      if (!value) return { isValid: false, message: 'You must agree to be contacted' };
      return { isValid: true };
    }

    if (isRequired && (!value || (typeof value === 'string' && !value.trim()))) {
      return {
        isValid: false,
        message: `${name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')} is required`,
      };
    }

    if (name === 'email' && value) return validateEmail(value);
    if (name === 'phoneNumber' && value) return validatePhone(value);
    return { isValid: true };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const validation = validateField(name, formData[name]);
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, [name]: validation.message }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    Object.keys(formData).forEach(key => {
      const validation = validateField(key, formData[key]);
      if (!validation.isValid) {
        newErrors[key] = validation.message;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const triggerDownload = (downloadUrl) => {
    if (downloadUrl && downloadUrl !== '#') {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadTriggered(true);
      setTimeout(() => setDownloadComplete(true), 1500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {};
    Object.keys(formData).forEach(key => { allTouched[key] = true; });
    setTouched(allTouched);

    if (!validateForm()) return;

    if (!leadMagnet || !leadMagnet.leadMagnetId) {
      setErrors({ submit: 'Lead magnet information is missing. Please refresh the page.' });
      return;
    }

    setSubmitting(true);

    try {
      const emailDomain = formData.email.split('@')[1]?.toLowerCase() || '';
      const leadCreateData = {
        lead_magnet_id: leadMagnet.leadMagnetId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        company_name: formData.companyName,
        job_title: formData.jobTitle || null,
        phone: formData.phoneNumber,
        email: formData.email,
        email_domain: emailDomain,
        country: formData.countryRegion || null,
        region_state: null,
        consent_given: formData.consentGiven,
        sales_notes: null,
        utm_campaign: null,
        utm_medium: null,
        utm_source: null,
      };

      await leadApi.leadCreatePost({ leadCreate: leadCreateData });

      const formConfig = leadMagnet?.formConfig || {};
      const downloadUrl = formConfig.downloadUrl;

      if (downloadUrl && downloadUrl !== '#') {
        triggerDownload(downloadUrl);
      }

      setSubmitted(true);
      window.history.replaceState(null, '', window.location.pathname + '?download=complete');
      trackLinkedInConversion(LINKEDIN_CONVERSIONS.DOWNLOAD_COMPLETE);
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.DOWNLOAD_COMPLETE);
    } catch (error) {
      console.error('Error submitting lead:', error);
      const errorMsg = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Network error. Please try again.';
      setErrors({ submit: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const formConfig = leadMagnet.formConfig || {};
    const downloadUrl = formConfig.downloadUrl || '#';
    const thankYouMessage = formConfig.thankYouMessage || 'Thank you for your interest!';
    const extraText = formConfig.thankYouExtraText || 'Check your inbox for a copy.';
    const emailSent = formConfig.emailLink !== false;

    return (
      <div className="lead-magnet-page">
        <div className="lead-magnet-header">
          <a href="/" className="logo" aria-label="CerebralMachines home">
            <img src="/CMLogo.png" alt="CerebralMachines" />
          </a>
        </div>

        <div className="lead-magnet-container lead-magnet-container--centered">
          <div className="thank-you-section">
            <div className="thank-you-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#10B981" />
                <path d="M20 32 L28 40 L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1>{thankYouMessage}</h1>
            {emailSent && (
              <p className="thank-you-email">
                A download link has been sent to <strong>{formData.email}</strong>
              </p>
            )}
            <p className="thank-you-extra">{extraText}</p>
            {downloadUrl && downloadUrl !== '#' && (
              <div className="download-actions">
                {!downloadTriggered && (
                  <a
                    href={downloadUrl}
                    className="btn-download"
                    download
                    onClick={(e) => { e.preventDefault(); triggerDownload(downloadUrl); }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 2v12M6 10l4 4 4-4M2 16h16" />
                    </svg>
                    Download Now
                  </a>
                )}
                {downloadTriggered && !downloadComplete && (
                  <div className="download-success download-success--pending">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="9" stroke="#6B7280" strokeWidth="2" />
                      <path d="M10 5v6M10 13v1" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Downloading...
                  </div>
                )}
                {downloadComplete && (
                  <div className="download-success">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="9" stroke="#10B981" strokeWidth="2" />
                      <path d="M6 10 L9 13 L14 7" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Download Complete
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const formConfig = leadMagnet.formConfig || {};
  const ctaButtonText = formConfig.ctaButtonText || 'Submit';

  return (
    <div className="lead-magnet-page">
      <div className="lead-magnet-header">
        <a href="/" className="logo" aria-label="CerebralMachines home">
          <img src="/CMLogo.png" alt="CerebralMachines" />
        </a>
      </div>

      <div className="lead-magnet-container">
        <section className="hero-section">
          <h1 className="page-title">{leadMagnet.pageTitle}</h1>
          <h2 className="main-headline">{leadMagnet.mainHeadline}</h2>
          {leadMagnet.subHeadline && (
            <p className="sub-headline">{leadMagnet.subHeadline}</p>
          )}

          {leadMagnet.benefits && leadMagnet.benefits.length > 0 && (
            <section className="benefits-section">
              <ul className="benefits-list">
                {leadMagnet.benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span className="benefit-text">{benefit}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {leadMagnet.credibilityText && (
            <section className="credibility-section">
              <p className="credibility-text">{leadMagnet.credibilityText}</p>
            </section>
          )}
        </section>

        <section className="form-section">
          <form onSubmit={handleSubmit} className="lead-capture-form">
            <div className="form-header">
              <h3 className="form-header-title">Get Your Free Download</h3>
              <p className="form-header-subtitle">Fill out the form below to access your resource</p>
            </div>

            {errors.submit && (
              <div className="alert alert-error">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M10 6 L10 11" stroke="currentColor" strokeWidth="2" />
                  <circle cx="10" cy="14" r="1" fill="currentColor" />
                </svg>
                {errors.submit}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('firstName')}
                  className={touched.firstName && errors.firstName ? 'error' : ''}
                />
                {touched.firstName && errors.firstName && (
                  <span className="error-message">{errors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('lastName')}
                  className={touched.lastName && errors.lastName ? 'error' : ''}
                />
                {touched.lastName && errors.lastName && (
                  <span className="error-message">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="companyName">
                Company Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                onBlur={() => handleBlur('companyName')}
                className={touched.companyName && errors.companyName ? 'error' : ''}
              />
              {touched.companyName && errors.companyName && (
                <span className="error-message">{errors.companyName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="jobTitle">
                Job Title {formConfig.jobTitleRequired && <span className="required">*</span>}
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                onBlur={() => handleBlur('jobTitle')}
                className={touched.jobTitle && errors.jobTitle ? 'error' : ''}
              />
              {touched.jobTitle && errors.jobTitle && (
                <span className="error-message">{errors.jobTitle}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                onBlur={() => handleBlur('phoneNumber')}
                placeholder="(555) 123-4567"
                className={touched.phoneNumber && errors.phoneNumber ? 'error' : ''}
              />
              {touched.phoneNumber && errors.phoneNumber && (
                <span className="error-message">{errors.phoneNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                className={touched.email && errors.email ? 'error' : ''}
              />
              {touched.email && errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="countryRegion">Country / Region</label>
              <select
                id="countryRegion"
                name="countryRegion"
                value={formData.countryRegion}
                onChange={handleChange}
                onBlur={() => handleBlur('countryRegion')}
                className={touched.countryRegion && errors.countryRegion ? 'error' : ''}
              >
                <option value="" disabled>Select your country / region</option>
                {COUNTRY_REGION_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {touched.countryRegion && errors.countryRegion && (
                <span className="error-message">{errors.countryRegion}</span>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="consentGiven"
                  checked={formData.consentGiven}
                  onChange={handleChange}
                  onBlur={() => handleBlur('consentGiven')}
                  className={touched.consentGiven && errors.consentGiven ? 'error' : ''}
                />
                <span>I agree to be contacted by CerebralMachines <span className="required">*</span></span>
              </label>
              {touched.consentGiven && errors.consentGiven && (
                <span className="error-message">{errors.consentGiven}</span>
              )}
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : ctaButtonText}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

export default StandardLandingTemplate;
